import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AppFooter from '../components/AppFooter';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { api } from '../services/api';
import {
  LogOut, BookOpen, Calendar, CalendarDays, Link2, RefreshCw, Settings,
  AlertTriangle, GraduationCap, CheckCircle2, Circle, Play, Square, Shield, ListTodo, Clock,
  BarChart3, Users, FileText,
} from 'lucide-react';

interface CredentialsInfo {
  linked: boolean;
  school_code?: string;
  login_email?: string;
  last_sync_at?: string | null;
  last_sync_error?: string | null;
  last_sync_duration_ms?: number | null;
}
interface HomeworkFile { url?: string; href?: string; path?: string; name?: string; label?: string; filename?: string; size?: number; }
interface HomeworkItem {
  id: string; external_id: string; title: string; due_at: string | null; teacher: string | null;
  back: boolean; started_at: string | null; completed_at: string | null;
  total_work_seconds: number; active_session_started_at: string | null;
  files_json: HomeworkFile[] | null;
}
interface EventItem { starts_at: string; ends_at: string | null; all_day: boolean; title: string; location: string | null; grade_level: number | null; }
interface EventsResponse { events: EventItem[]; user_grade: number | null; mode: string; }
interface SubItem { date: string; hour: number; uf: string | null; vertr_uf: string | null; room: string | null; reason: string | null; text: string | null; abs_teacher: string | null; }
interface Todo {
  id: string; title: string; due_at: string | null; completed_at: string | null;
  priority: number; subject_code: string | null; active_session_started_at: string | null;
  total_work_seconds: number;
}

function formatDateTime(iso: string, allDay = false): string {
  const d = new Date(iso);
  if (allDay) return d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
  return d.toLocaleString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
}
function formatDuration(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}
function LiveTimer({ start }: { start: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  return <span className="text-yellow-300 font-mono text-xs">{formatDuration(Math.max(0, Math.floor((now - new Date(start).getTime()) / 1000)))}</span>;
}

const DashboardPage: React.FC = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [creds, setCreds] = useState<CredentialsInfo | null>(null);
  const [homework, setHomework] = useState<HomeworkItem[]>([]);
  const [events, setEvents] = useState<EventItem[]>([]);
  const [userGrade, setUserGrade] = useState<number | null>(null);
  const [showAllGrades, setShowAllGrades] = useState(false);
  const [subs, setSubs] = useState<SubItem[]>([]);
  const [todos, setTodos] = useState<Todo[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncSummary, setSyncSummary] = useState<string | null>(null);

  const isAdmin = user?.role === 'admin';

  const handleLogout = () => { logout(); navigate('/login'); };

  const loadAll = useCallback(async () => {
    try {
      const [c, h, e, s, t] = await Promise.all([
        api<CredentialsInfo>('/api/portal/credentials'),
        api<{ homework: HomeworkItem[] }>('/api/homework'),
        api<EventsResponse>(`/api/events?grades=${showAllGrades ? 'all' : 'own'}`),
        api<{ substitutions: SubItem[] }>('/api/substitutions'),
        api<{ todos: Todo[] }>('/api/todos'),
      ]);
      setCreds(c); setHomework(h.homework); setEvents(e.events);
      setUserGrade(e.user_grade); setSubs(s.substitutions); setTodos(t.todos);
    } catch (err: any) { console.error(err); }
  }, [showAllGrades]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const triggerSync = useCallback(async () => {
    setBusy('sync'); setSyncError(null); setSyncSummary(null);
    try {
      const r = await api<{ ok: boolean; counts: any; duration_ms: number }>('/api/portal/sync', { method: 'POST' });
      setSyncSummary(
        `${r.counts.timetableEntries} Stunden · ${r.counts.homeworkNew} neue HA · ${r.counts.eventsNew} neue Termine · ${r.counts.substitutionsNew} Vertretungen (${Math.round(r.duration_ms / 100) / 10}s)`
      );
      await loadAll();
    } catch (err: any) { setSyncError(err?.message || 'Sync fehlgeschlagen'); }
    finally { setBusy(null); }
  }, [loadAll]);

  const hwToggleComplete = async (h: HomeworkItem) => {
    setBusy(`hw-${h.id}`);
    try { await api(`/api/homework/${h.id}/complete`, { method: 'POST', body: JSON.stringify({ completed: !h.completed_at }) }); await loadAll(); }
    catch {} finally { setBusy(null); }
  };
  const hwToggleTimer = async (h: HomeworkItem) => {
    setBusy(`hw-${h.id}`);
    try { await api(`/api/homework/${h.id}/${h.active_session_started_at ? 'stop' : 'start'}`, { method: 'POST' }); await loadAll(); }
    catch {} finally { setBusy(null); }
  };

  const todoToggleComplete = async (t: Todo) => {
    setBusy(`todo-${t.id}`);
    try { await api(`/api/todos/${t.id}/complete`, { method: 'POST', body: JSON.stringify({ completed: !t.completed_at }) }); await loadAll(); }
    catch {} finally { setBusy(null); }
  };
  const todoToggleTimer = async (t: Todo) => {
    setBusy(`todo-${t.id}`);
    try { await api(`/api/todos/${t.id}/${t.active_session_started_at ? 'stop' : 'start'}`, { method: 'POST' }); await loadAll(); }
    catch {} finally { setBusy(null); }
  };

  const upcomingHomework = useMemo(() => homework.slice(0, 5), [homework]);
  const upcomingEvents = useMemo(() => events.slice(0, 5), [events]);
  const upcomingTodos = useMemo(() => todos.slice(0, 5), [todos]);

  return (
    <div className="min-h-screen bg-gray-900">
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-blue-500" />
            <h1 className="text-2xl font-bold text-white">Hausaufgabenheft</h1>
          </div>
          <nav className="hidden md:flex items-center gap-4 text-sm">
            <Link to="/stundenplan" className="text-gray-300 hover:text-white flex items-center gap-1"><CalendarDays className="w-4 h-4" /> Stundenplan</Link>
            <Link to="/hausaufgaben" className="text-gray-300 hover:text-white flex items-center gap-1"><BookOpen className="w-4 h-4" /> Hausaufgaben</Link>
            <Link to="/manuelle-hausaufgaben" className="text-gray-300 hover:text-white flex items-center gap-1"><Users className="w-4 h-4" /> Manuelle HA</Link>
            <Link to="/arbeiten" className="text-gray-300 hover:text-white flex items-center gap-1"><FileText className="w-4 h-4" /> Arbeiten</Link>
            <Link to="/statistik" className="text-gray-300 hover:text-white flex items-center gap-1"><BarChart3 className="w-4 h-4" /> Statistik</Link>
            <Link to="/todos" className="text-gray-300 hover:text-white flex items-center gap-1"><ListTodo className="w-4 h-4" /> TODOs</Link>
            <Link to="/noten" className="text-gray-300 hover:text-white flex items-center gap-1"><GraduationCap className="w-4 h-4" /> Noten</Link>
            <Link to="/termine" className="text-gray-300 hover:text-white flex items-center gap-1"><Calendar className="w-4 h-4" /> Termine</Link>
            <Link to="/einstellungen" className="text-gray-300 hover:text-white flex items-center gap-1"><Settings className="w-4 h-4" /> Einstellungen</Link>
            {isAdmin && <Link to="/admin/invites" className="text-pink-400 hover:text-pink-300 flex items-center gap-1"><Shield className="w-4 h-4" /> Admin</Link>}
          </nav>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm text-gray-400">{user?.role}{user && 'class' in user ? '' : ''}</p>
              <p className="text-white font-semibold">{user?.name}</p>
            </div>
            <button onClick={handleLogout} className="flex items-center gap-2 bg-gray-700 hover:bg-red-600 text-white px-3 py-2 rounded-lg transition text-sm">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Sync card */}
        <section className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <Link2 className="w-5 h-5 text-blue-400" /> Schul-Portal-Import
              </h2>
              {creds && creds.linked ? (
                <div className="text-gray-400 text-sm mt-1">
                  Verknüpft mit <code className="text-gray-300">{creds.school_code}</code> als <code className="text-gray-300">{creds.login_email}</code>.{' '}
                  {creds.last_sync_at
                    ? <>Letzter Sync: <span className="text-gray-200">{formatDateTime(creds.last_sync_at)}</span></>
                    : <>Noch nie synchronisiert.</>}
                </div>
              ) : (
                <p className="text-gray-400 text-sm mt-1">
                  Noch kein Schul-Portal verknüpft. <Link to="/portal" className="text-blue-400 hover:text-blue-300">Jetzt einrichten</Link>.
                </p>
              )}
            </div>
            <button
              disabled={!creds?.linked || busy === 'sync'}
              onClick={triggerSync}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${busy === 'sync' ? 'animate-spin' : ''}`} />
              {busy === 'sync' ? 'Synchronisiert…' : 'Jetzt synchronisieren'}
            </button>
          </div>
          {syncSummary && <p className="mt-3 text-sm text-green-400">✅ {syncSummary}</p>}
          {syncError && <p className="mt-3 text-sm text-red-400">⚠️ {syncError}</p>}
          {creds?.last_sync_error && !syncError && !syncSummary && (
            <p className="mt-3 text-sm text-red-400">⚠️ Letzter Fehler: {creds.last_sync_error}</p>
          )}
        </section>

        {/* Substitutions banner */}
        {subs.length > 0 && (
          <section className="bg-yellow-500/10 border border-yellow-500/40 rounded-lg p-4">
            <h3 className="text-yellow-300 font-semibold flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4" /> Aktuelle Vertretungen
            </h3>
            <ul className="space-y-1 text-sm text-yellow-100">
              {subs.slice(0, 6).map((s, i) => (
                <li key={i}>
                  <span className="font-mono text-yellow-300">{formatDate(s.date)} · {s.hour}.</span>{' '}
                  {s.uf ?? '—'} → <strong>{s.vertr_uf ?? '—'}</strong>
                  {s.room && <> · Raum {s.room}</>}
                  {s.abs_teacher && <> · statt {s.abs_teacher}</>}
                  {s.text && <> · {s.text}</>}
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Homework */}
          <section className="bg-gray-800 border border-gray-700 rounded-lg p-6 lg:col-span-2">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-blue-400" /> Offene Hausaufgaben
              </h2>
              <Link to="/hausaufgaben" className="text-sm text-blue-400 hover:text-blue-300">Alle →</Link>
            </div>
            {upcomingHomework.length === 0 ? (
              <p className="text-gray-500 text-sm">Keine offenen Hausaufgaben.</p>
            ) : (
              <ul className="space-y-3">
                {upcomingHomework.map(h => (
                  <li key={h.id} className="border-l-2 border-blue-500 pl-3 flex items-start gap-3">
                    <button onClick={() => hwToggleComplete(h)} disabled={busy === `hw-${h.id}`} className="mt-1">
                      {h.completed_at
                        ? <CheckCircle2 className="w-4 h-4 text-green-400" />
                        : <Circle className="w-4 h-4 text-gray-500 hover:text-gray-300" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="text-white whitespace-pre-wrap text-sm">{h.title}</div>
                      <div className="text-xs text-gray-400 mt-1 flex flex-wrap gap-3">
                        {h.due_at && <>📅 {formatDate(h.due_at)}</>}
                        {h.teacher && <>{h.teacher}</>}
                        {h.total_work_seconds > 0 && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDuration(h.total_work_seconds)}</span>}
                        {h.active_session_started_at && <LiveTimer start={h.active_session_started_at} />}
                        {h.files_json && h.files_json.length > 0 && (
                          <span className="flex items-center gap-1 text-blue-300">📎 {h.files_json.length}</span>
                        )}
                      </div>
                    </div>
                    {!h.completed_at && (
                      <button onClick={() => hwToggleTimer(h)} disabled={busy === `hw-${h.id}`}
                        className={`p-1.5 rounded transition ${h.active_session_started_at ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-gray-700 hover:bg-gray-600'}`}
                        title={h.active_session_started_at ? 'Timer stoppen' : 'Timer starten'}>
                        {h.active_session_started_at ? <Square className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Todos */}
          <section className="bg-gray-800 border border-gray-700 rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <ListTodo className="w-5 h-5 text-green-400" /> Meine Aufgaben
              </h2>
              <Link to="/todos" className="text-sm text-green-400 hover:text-green-300">Alle →</Link>
            </div>
            {upcomingTodos.length === 0 ? (
              <p className="text-gray-500 text-sm">Keine offenen TODOs. <Link to="/todos" className="text-green-400">Neue anlegen →</Link></p>
            ) : (
              <ul className="space-y-3">
                {upcomingTodos.map(t => (
                  <li key={t.id} className="border-l-2 border-green-500 pl-3 flex items-start gap-3">
                    <button onClick={() => todoToggleComplete(t)} disabled={busy === `todo-${t.id}`} className="mt-1">
                      {t.completed_at
                        ? <CheckCircle2 className="w-4 h-4 text-green-400" />
                        : <Circle className="w-4 h-4 text-gray-500 hover:text-gray-300" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm">{t.title}</div>
                      <div className="text-xs text-gray-400 mt-1 flex flex-wrap gap-3">
                        {t.due_at && <>📅 {formatDate(t.due_at)}</>}
                        {t.subject_code && <span className="bg-gray-700 px-1.5 py-0.5 rounded">{t.subject_code}</span>}
                        {t.total_work_seconds > 0 && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDuration(t.total_work_seconds)}</span>}
                        {t.active_session_started_at && <LiveTimer start={t.active_session_started_at} />}
                      </div>
                    </div>
                    {!t.completed_at && (
                      <button onClick={() => todoToggleTimer(t)} disabled={busy === `todo-${t.id}`}
                        className={`p-1.5 rounded transition ${t.active_session_started_at ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-gray-700 hover:bg-gray-600'}`}>
                        {t.active_session_started_at ? <Square className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Events */}
          <section className="bg-gray-800 border border-gray-700 rounded-lg p-6 lg:col-span-3">
            <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-400" /> Nächste Termine
                {userGrade !== null && !showAllGrades && <span className="text-xs bg-purple-500/20 text-purple-200 px-2 py-0.5 rounded">Jgst. {userGrade}</span>}
              </h2>
              <div className="flex items-center gap-3 text-sm">
                <label className="flex items-center gap-1 text-gray-400">
                  <input type="checkbox" checked={showAllGrades} onChange={e => setShowAllGrades(e.target.checked)} className="accent-blue-500" />
                  alle Stufen
                </label>
                <Link to="/termine" className="text-purple-400 hover:text-purple-300">Alle →</Link>
              </div>
            </div>
            {upcomingEvents.length === 0 ? (
              <p className="text-gray-500 text-sm">Keine anstehenden Termine.</p>
            ) : (
              <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {upcomingEvents.map((e, i) => (
                  <li key={i} className="border-l-2 border-purple-500 pl-3">
                    <div className="text-white text-sm">{e.title}</div>
                    <div className="text-xs text-gray-400 mt-1 flex flex-wrap gap-2">
                      <span>{formatDateTime(e.starts_at, e.all_day)}</span>
                      {e.location && <>· {e.location}</>}
                      {e.grade_level !== null && <span className="bg-purple-500/20 text-purple-200 px-1.5 py-0.5 rounded">Jgst. {e.grade_level}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <section className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Schnellzugriff</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {[
              { to: '/stundenplan', Icon: CalendarDays, label: 'Stundenplan', color: 'text-blue-400' },
              { to: '/hausaufgaben', Icon: BookOpen, label: 'Hausaufgaben', color: 'text-blue-400' },
              { to: '/manuelle-hausaufgaben', Icon: Users, label: 'Manuelle HA', color: 'text-indigo-400' },
              { to: '/arbeiten', Icon: FileText, label: 'Arbeiten', color: 'text-orange-400' },
              { to: '/statistik', Icon: BarChart3, label: 'Statistik', color: 'text-cyan-400' },
              { to: '/todos', Icon: ListTodo, label: 'TODOs', color: 'text-green-400' },
              { to: '/noten', Icon: GraduationCap, label: 'Noten', color: 'text-green-400' },
              { to: '/termine', Icon: Calendar, label: 'Termine', color: 'text-purple-400' },
              { to: '/portal', Icon: Link2, label: 'Schul-Portal', color: 'text-blue-400' },
              { to: '/einstellungen', Icon: Settings, label: 'Einstellungen', color: 'text-gray-400' },
            ].map(({ to, Icon, label, color }) => (
              <Link key={to} to={to} className="bg-gray-700 hover:bg-gray-600 rounded-lg p-4 text-center text-white">
                <Icon className={`w-6 h-6 mx-auto mb-2 ${color}`} />
                <div className="text-sm font-medium">{label}</div>
              </Link>
            ))}
            {isAdmin && (
              <Link to="/admin/invites" className="bg-pink-600/20 hover:bg-pink-600/30 border border-pink-500/40 rounded-lg p-4 text-center text-white">
                <Shield className="w-6 h-6 mx-auto mb-2 text-pink-400" />
                <div className="text-sm font-medium">Admin</div>
              </Link>
            )}
          </div>
        </section>
        <AppFooter />
      </main>
    </div>
  );
};

export default DashboardPage;
